import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faSun,
  faMoon,
  faRightFromBracket,
  faChevronDown,
  faUser,
  faAddressCard,
} from '@fortawesome/free-solid-svg-icons';
import { useThemeContext } from '@/providers/ThemeProvider';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { useUiStore } from '@/stores/ui.store';
import { platformAuthService } from '@/services/platform-auth.service';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { toast } from '@/lib/swal';

export function PlatformHeader() {
  const { resolvedMode, setMode } = useThemeContext();
  const user = usePlatformAuthStore((s) => s.user);
  const clearAuth = usePlatformAuthStore((s) => s.clearAuth);
  const refreshToken = usePlatformAuthStore((s) => s.refreshToken);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = user?.displayName ?? user?.email ?? 'Platform User';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase() || 'P';

  /**
   * Phase 12.2A (F-01) — real server-side logout.
   *
   * The refresh token is now passed to the API so the session is genuinely
   * revoked. Local state is still cleared unconditionally: the user asked to
   * sign out of this browser and must not be left in a half-signed-in state
   * because the network failed.
   *
   * What changed is that a failure is no longer SILENT. Previously the 400 this
   * call always returned was swallowed by a bare `catch`, so nothing —
   * not the user, not the console, not any monitoring — could tell that the
   * server-side session had survived. A failure is now surfaced to the user and
   * logged, because "you are signed out here, but the session may still be
   * live" is a materially different outcome from a clean logout.
   */
  const handleLogout = async () => {
    let revoked = false;
    try {
      if (refreshToken) {
        await platformAuthService.logout(refreshToken);
        revoked = true;
      }
    } catch {
      revoked = false;
    }

    if (!revoked) {
      toast.error('Signed out on this device, but the server session could not be revoked.');
    }

    clearAuth();
    navigate(PLATFORM_ROUTES.LOGIN);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/[0.07] hover:text-foreground lg:hidden"
        aria-label="Open navigation"
      >
        <FontAwesomeIcon icon={faBars} className="h-4 w-4" />
      </button>

      {/* Platform context badge */}
      <div className="hidden items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/[0.08] px-3 py-1.5 text-xs lg:flex">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        <span className="font-semibold tracking-wide text-primary">PLATFORM CONSOLE</span>
      </div>

      <div className="hidden flex-1 lg:block" />

      <div className="flex items-center gap-2">
        <button
          onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/[0.07] hover:text-foreground"
          aria-label={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <FontAwesomeIcon icon={resolvedMode === 'dark' ? faSun : faMoon} className="h-4 w-4" />
        </button>

        {user ? (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex h-9 items-center gap-2 rounded-lg px-2 text-sm text-foreground transition-colors hover:bg-primary/[0.07]"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {initials}
              </div>
              <span className="hidden sm:block max-w-[120px] truncate font-medium">
                {displayName}
              </span>
              <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-muted-foreground" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-xl border border-border bg-card shadow-lg">
                <div className="border-b border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{displayName}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  {user.role && (
                    <p className="mt-0.5 text-xs font-medium text-primary">{user.role}</p>
                  )}
                </div>
                <div className="p-1">
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate(PLATFORM_ROUTES.PROFILE); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <FontAwesomeIcon icon={faAddressCard} className="h-3.5 w-3.5 text-muted-foreground" />
                    My Profile
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); void handleLogout(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10"
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} className="h-3.5 w-3.5" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate(PLATFORM_ROUTES.LOGIN)}
            className="flex h-9 items-center gap-2 rounded-lg px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
            <span>Sign in</span>
          </button>
        )}
      </div>
    </header>
  );
}
